import { useEffect, useState } from 'react'
import {
  getOptionsByGroup,
  optionCodes,
  optionsToLabelMap,
  type TicketOption,
} from '@/lib/services/ticketOptionListService'

export type UseTicketOptionsResult = {
  options: TicketOption[]
  codes: string[]
  labelMap: Record<string, string>
  loading: boolean
  error: string | null
}

const cache = new Map<string, TicketOption[]>()
const inflight = new Map<string, Promise<TicketOption[]>>()

function loadGroup(groupCode: string): Promise<TicketOption[]> {
  const cached = cache.get(groupCode)
  if (cached) return Promise.resolve(cached)

  const pending = inflight.get(groupCode)
  if (pending) return pending

  const promise = getOptionsByGroup(groupCode)
    .then((rows) => {
      cache.set(groupCode, rows)
      return rows
    })
    .finally(() => {
      inflight.delete(groupCode)
    })

  inflight.set(groupCode, promise)
  return promise
}

/**
 * Load ticket option list values for a group_code from
 * GET /options/ticket-option-lists/{groupCode}.
 */
export function useTicketOptions(groupCode: string): UseTicketOptionsResult {
  const [options, setOptions] = useState<TicketOption[]>(() => cache.get(groupCode) ?? [])
  const [loading, setLoading] = useState(() => !cache.has(groupCode))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(!cache.has(groupCode))
    loadGroup(groupCode)
      .then((rows) => {
        if (!cancelled) {
          setOptions(rows)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setOptions([])
          setError(e instanceof Error ? e.message : 'Failed to load options')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [groupCode])

  return {
    options,
    codes: optionCodes(options),
    labelMap: optionsToLabelMap(options),
    loading,
    error,
  }
}
